import { Controller } from "stimulus";

// Drives the redesigned classroom control dock (mic / camera / screen / hand /
// recording / leave) by proxying to the embedded Jitsi instance via executeCommand.
// Shared by the professor desktop view and the student mobile view.
//
// The Jitsi api is delivered by the sibling meeting--moderator / meeting--guest
// controller through the `jitsi:start` CustomEvent (api lives in event.detail.api).
//
// Each toggle button declares the class it should flip via data attributes:
//   data-off-class="is-off"  → applied while the feature is OFF (mic/cam)
//   data-on-class="is-on"    → applied while the feature is ON  (screen/hand/record)
export default class extends Controller {
  static targets = ["mic", "cam", "screen", "hand", "record", "timer", "count"];

  static values = {
    // epoch milliseconds the class started at; 0 → start counting from join time
    startTime: Number,
    totalGuests: Number
  };

  connect() {
    this.onJitsiStart = this.onJitsiStart.bind(this);
    this.element.addEventListener("jitsi:start", this.onJitsiStart);
    // Full-screen takeover: hide the portal chrome (top bar / bottom nav) so it
    // can't overlap the classroom dock on mobile. Scoped via CSS to the portal layout.
    document.body.classList.add("classroom-immersive");
  }

  disconnect() {
    this.element.removeEventListener("jitsi:start", this.onJitsiStart);
    clearInterval(this.timerInterval);
    document.body.classList.remove("classroom-immersive");
    this.element.classList.remove("classroom-joined");
  }

  onJitsiStart(event) {
    this.api = event.detail && event.detail.api;
    if (!this.api) return;

    this.bindStatusListeners();
    this.syncInitialState();
    this.startTimer();
    this.refreshCount();
  }

  // ---------- dock actions ----------
  toggleMic() {
    this.api && this.api.executeCommand("toggleAudio");
  }

  toggleCam() {
    this.api && this.api.executeCommand("toggleVideo");
  }

  toggleScreen() {
    this.api && this.api.executeCommand("toggleShareScreen");
  }

  toggleHand() {
    this.api && this.api.executeCommand("toggleRaiseHand");
  }

  toggleRecord() {
    if (!this.api) return;

    // Recording requires Jibri configured on the Jitsi deployment + JWT perms.
    // If unavailable the command no-ops; we degrade gracefully.
    if (this.isRecording) {
      this.api.executeCommand("stopRecording", "file");
    } else {
      this.api.executeCommand("startRecording", { mode: "file" });
    }
  }

  leave() {
    this.api && this.api.executeCommand("hangup");
  }

  // ---------- state sync ----------
  bindStatusListeners() {
    this.api.addEventListener("videoConferenceJoined", (event) => {
      this.localId = event.id;
      // Reveal the custom dock only after the student actually joins. During the
      // prejoin/lobby phase the dock is not functional yet and would overlap
      // Jitsi's own prejoin controls (mobile scope hides it via CSS until now).
      this.element.classList.add("classroom-joined");
      this.refreshCount();
    });
    this.api.addEventListener("participantJoined", () => this.refreshCount());
    this.api.addEventListener("participantLeft", () => this.refreshCount());

    this.api.addEventListener("audioMuteStatusChanged", (event) => {
      this.setOff(this.micTarget, event.muted);
    });
    this.api.addEventListener("videoMuteStatusChanged", (event) => {
      this.setOff(this.camTarget, event.muted);
    });
    this.api.addEventListener("screenSharingStatusChanged", (event) => {
      this.hasScreenTarget && this.setOn(this.screenTarget, event.on);
    });
    this.api.addEventListener("raiseHandUpdated", (event) => {
      if (this.localId && event.id !== this.localId) return;
      this.hasHandTarget && this.setOn(this.handTarget, !!event.handRaised);
    });
    this.api.addEventListener("recordingStatusChanged", (event) => {
      this.isRecording = !!event.on;
      this.hasRecordTarget && this.setOn(this.recordTarget, this.isRecording);
    });
  }

  syncInitialState() {
    if (this.hasMicTarget && this.api.isAudioMuted) {
      this.api.isAudioMuted().then((muted) => this.setOff(this.micTarget, muted));
    }
    if (this.hasCamTarget && this.api.isVideoMuted) {
      this.api.isVideoMuted().then((muted) => this.setOff(this.camTarget, muted));
    }
  }

  setOff(button, isOff) {
    if (!button) return;
    const offClass = button.dataset.offClass || "is-off";
    button.classList.toggle(offClass, !!isOff);
  }

  setOn(button, isOn) {
    if (!button) return;
    const onClass = button.dataset.onClass || "is-on";
    button.classList.toggle(onClass, !!isOn);
  }

  // ---------- live timer ----------
  startTimer() {
    const baseMs = this.startTimeValue > 0 ? this.startTimeValue : Date.now();
    this.timerStartedAt = baseMs;
    this.tickTimer();
    this.timerInterval = setInterval(() => this.tickTimer(), 1000);
  }

  tickTimer() {
    if (!this.hasTimerTarget) return;

    const elapsedSeconds = Math.max(0, Math.floor((Date.now() - this.timerStartedAt) / 1000));
    const hours = Math.floor(elapsedSeconds / 3600);
    const minutes = Math.floor((elapsedSeconds % 3600) / 60);
    const seconds = elapsedSeconds % 60;
    const pad = (value) => String(value).padStart(2, "0");

    this.timerTarget.textContent = hours > 0
      ? `${hours}:${pad(minutes)}:${pad(seconds)}`
      : `${pad(minutes)}:${pad(seconds)}`;
  }

  // ---------- online count ----------
  refreshCount() {
    if (!this.hasCountTarget || !this.api || !this.api.getNumberOfParticipants) return;

    const online = this.api.getNumberOfParticipants();
    this.countTarget.textContent = this.hasTotalGuestsValue && this.totalGuestsValue > 0
      ? `${online} de ${this.totalGuestsValue} online`
      : `${online} online`;
  }
}
